import { INBOUND_MANIFEST_TYPES, PALLET_STATUSES, RECEIVED_STATUSES } from '@/constants/manifestStatuses';

/** Resolve pallet id from a manifest line item (pallet_id may only exist on nested pallet). */
export const getItemPalletId = (item) => item?.pallet_id ?? item?.pallet?.pallet_id;

/**
 * The pallet an item gets when it is received without one — a pallet of one, no bin yet. Receiving,
 * assigning a location, and an order-details receive all mint the same thing.
 */
export const singlePalletAtReceived = (warehouse_id = null) => ({
    type: 'SINGLE',
    warehouse_id,
    warehouse_location: null,
    warehouse_status: PALLET_STATUSES.RECEIVED,
});

/**
 * Warehouse an order is being received into. An inbound manifest is the only order-scoped record of
 * one, so this is null for an order that isn't on one — pallet_items.warehouse_id is nullable.
 * Null too when the order spans warehouses: WarehousingCard declines to guess in that case, and a
 * wrong warehouse silently files the pallet under the wrong rack list at bin-assignment time.
 */
export const orderWarehouseId = (order) => {
    const warehouseIds = new Set();

    (order?.itemsByOrderId || []).forEach((item) => {
        (item?.manifests || []).forEach((mapping) => {
            const manifest = mapping?.manifest;
            if (manifest?.warehouse_id && INBOUND_MANIFEST_TYPES.includes(manifest.type)) {
                warehouseIds.add(manifest.warehouse_id);
            }
        });
    });

    return warehouseIds.size === 1 ? [...warehouseIds][0] : null;
};

/**
 * Pallet writes that make an OMS receive visible to the WMS, which reads received off the pallet and
 * nothing else. Pallet-less items get their own SINGLE pallet (matching Manifest's receiveItem);
 * items already on a pallet have it flipped; already-received items are left alone so a re-submit
 * can't demote a stored or staged pallet back to RECEIVED. Items `canReceive` rejects are left alone.
 */
export const buildReceivePalletWrites = (order, { canReceive = () => true } = {}) => {
    const warehouse_id = orderWarehouseId(order);
    const items = [];
    const pallet_ids = new Set();

    (order?.itemsByOrderId || []).forEach((item) => {
        if (!item?.item_id || RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status)) return;
        if (!canReceive(item)) return;

        const palletId = getItemPalletId(item);
        if (palletId) {
            pallet_ids.add(palletId);
            return;
        }

        items.push({ item_id: item.item_id, pallet: { data: singlePalletAtReceived(warehouse_id) } });
    });

    return { items, pallet_ids: [...pallet_ids] };
};

/** Orders with at least one manifest line on the given pallet — a pallet can span orders. */
export const ordersOnPallet = (orders, palletId) =>
    (orders || []).filter((order) => (order.manifestItems || []).some((item) => getItemPalletId(item) === palletId));

/**
 * True when receiving everything in `batch` leaves none of the order's items outstanding. The batch is
 * the whole selection, not one item: mutations resolve after the loop that fires them, so a per-item
 * gate reads a snapshot in which no sibling is received yet. Gates the order's RECEIVED event.
 */
export const isOrderFullyReceivedBy = (order, { itemIds = [], palletIds = [] } = {}) => {
    const items = new Set((itemIds || []).filter(Boolean).map((id) => String(id)));
    const pallets = new Set((palletIds || []).filter(Boolean).map((id) => String(id)));

    return (order?.itemsByOrderId || []).every(
        (item) =>
            items.has(String(item?.item_id)) ||
            pallets.has(String(getItemPalletId(item))) ||
            RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status)
    );
};

/** Single-pallet form, for the one-pallet receive paths. */
export const isOrderReceivedByPallet = (order, palletId) => isOrderFullyReceivedBy(order, { palletIds: [palletId] });

/**
 * Of `candidates`, the orders whose RECEIVED events this call should write. `completesOrderIds` from
 * the bulk plan is authoritative; a single receive has no batch and judges the order on its own.
 */
export const ordersCompletedByReceive = (candidates, { completesOrderIds, batch } = {}) =>
    (candidates || []).filter((order) =>
        completesOrderIds ? completesOrderIds.includes(order?.order_id) : isOrderFullyReceivedBy(order, batch)
    );

/**
 * `Map<item_id, order_id[]>` — which orders each item of a bulk receive completes. Gates once against
 * the whole selection so an order finished by several items at a time is recognised, and claims each
 * order for exactly one item so that doesn't become one duplicate RECEIVED event per item.
 */
export const planBulkReceive = (allOrders, items = []) => {
    const batch = {
        itemIds: (items || []).map((item) => item?.item_id),
        palletIds: (items || []).map((item) => getItemPalletId(item)),
    };

    const claimed = new Set();

    return (items || []).reduce((plan, item) => {
        if (!item) return plan;

        const palletId = getItemPalletId(item);
        const onPallet = palletId ? ordersOnPallet(allOrders, palletId) : [];
        const candidates = onPallet.length ? onPallet : [item.order].filter(Boolean);

        const completes = candidates
            .filter((order) => order?.order_id && !claimed.has(order.order_id))
            .filter((order) => isOrderFullyReceivedBy(order, batch))
            .map((order) => order.order_id);

        completes.forEach((order_id) => claimed.add(order_id));

        return plan.set(item.item_id, completes);
    }, new Map());
};

/**
 * Manifest line items sitting on any of the given pallets — a pallet can span orders, so every order's
 * lines are searched. Reads the already-loaded manifest rather than issuing a lookup. Ids compare as
 * strings because they arrive both from the Apollo cache and from `_set` payloads.
 */
export const manifestItemsOnPallets = (orders, palletIds) => {
    const wanted = new Set(
        (Array.isArray(palletIds) ? palletIds : [palletIds]).filter(Boolean).map((id) => String(id))
    );
    if (!wanted.size) {
        return [];
    }

    return (orders || []).flatMap((order) =>
        (order?.manifestItems || []).filter((item) => wanted.has(String(getItemPalletId(item))))
    );
};

/** Unique pallets referenced by manifest lines across orders. */
export const collectManifestPalletsFromOrders = (orders) => [
    ...new Map(
        (orders || []).flatMap((order) =>
            (order.manifestItems || []).flatMap((item) => {
                const palletId = getItemPalletId(item);
                return palletId && item.pallet ? [[palletId, item.pallet]] : [];
            })
        )
    ).values(),
];
